import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getPublicLetter, submitExternalSignature } from "../../../Redux/Reducers/letterHeadReducer.ts";
import { PenTool, Upload, CheckCircle2, Loader2, FileSignature, XCircle, ShieldCheck, Fingerprint } from "lucide-react";
import SignatureModal from "./components/letter/SignatureModal";
import LetterPreview from "./components/letter/LetterPreview";

export default function ExternalSignPage() {
  const [searchParams] = useSearchParams();
  const letterId = searchParams.get("letterId");
  const token = searchParams.get("token");
  const dispatch = useDispatch();
  const BACKEND_URL = process.env.REACT_APP_ENTERPRISE_BACKEND_URL || "http://localhost:4040";
  
  const { publicLetter: letter } = useSelector((state) => state.letterHead);
  
  const [isInitLoading, setIsInitLoading] = useState(true); 
  const [localError, setLocalError] = useState(null);
  
  const [activeTab, setActiveTab] = useState("draw"); 
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [drawnSignature, setDrawnSignature] = useState(null);
  const [esignData, setEsignData] = useState(null); 
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
  const [isDigiLockerLoading, setIsDigiLockerLoading] = useState(false);

  const [isAlreadySignedError, setIsAlreadySignedError] = useState(false);

  // CHECK IF ALREADY SIGNED
  const isAlreadySigned = useMemo(() => {
    if (isAlreadySignedError) return true;
    if (!letter) return false;
    const sigs = letter.signatories || letter.LetterSignatories || [];
    const currentSig = sigs.find(s => s.unique_token === token);
    
    return currentSig && currentSig.status === 'SIGNED';
  }, [letter, token, isAlreadySignedError]);

  // SMART DATE CALCULATOR
  const exactSignedDate = useMemo(() => {
    if (isSuccess) return new Date();
    if (letter) {
        const sigs = letter.signatories || letter.LetterSignatories || [];
        const currentSig = sigs.find(s => s.unique_token === token);
        
        if (currentSig && currentSig.status === 'SIGNED') {
            const actualDate = 
                currentSig.updatedAt || 
                currentSig.updated_at || 
                currentSig.signed_at || 
                currentSig.createdAt || 
                currentSig.created_at;
            if (actualDate) return new Date(actualDate);
        }
    }
    return null;
  }, [letter, token, isSuccess]);

  const formattedDate = exactSignedDate 
    ? new Date(exactSignedDate).toLocaleString("en-US", { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })
    : null;

  // Fetch Document
  useEffect(() => {
    if (!letterId) {
      setLocalError("No document ID provided in the secure link.");
      setIsInitLoading(false);
      return;
    }
    
    setLocalError(null);
    setIsAlreadySignedError(false);

    dispatch(getPublicLetter(letterId))
      .unwrap()
      .catch((err) => {
        const rawError = err?.error || err?.message || err || "";
        const safeErrMsg = String(rawError).toLowerCase();
        
        if (
            safeErrMsg.includes("already signed") || 
            safeErrMsg.includes("rejected") || 
            safeErrMsg.includes("used") || 
            safeErrMsg.includes("expired")
        ) {
            setIsAlreadySignedError(true);
        } else {
            setLocalError(rawError || "Failed to load document");
        }
      })
      .finally(() => {
        setIsInitLoading(false); 
      });
  }, [letterId, token, dispatch]);

  useEffect(() => {
    if (!uploadFile) { setUploadPreview(null); return; }
    const objectUrl = URL.createObjectURL(uploadFile);
    setUploadPreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [uploadFile]);

  // DIGILOCKER POPUP HANDLER
  const handleDigiLockerInit = async () => {
    setIsDigiLockerLoading(true);
    try {
        const res = await fetch(`${BACKEND_URL}/api/esign/initiate`);
        const data = await res.json();
        if (data.url) {
            sessionStorage.setItem("dl_verifier", data.code_verifier);
            window.open(data.url, "DigiLocker", "width=600,height=700,left=200,top=200");
        } else {
            alert("Failed to initialize DigiLocker.");
            setIsDigiLockerLoading(false);
        }
    } catch (e) {
        alert("Server connection failed.");
        setIsDigiLockerLoading(false);
    }
  };

  // LISTEN FOR POPUP SUCCESS
  useEffect(() => {
    const handleMessage = async (event) => {
        if (event.data?.type === 'DIGILOCKER_SUCCESS') {
            const code = event.data.code;
            const verifier = sessionStorage.getItem("dl_verifier");
            try {
                const res = await fetch(`${BACKEND_URL}/api/esign/verify-digilocker`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ code, code_verifier: verifier })
                });
                const result = await res.json();
                if (result.success) {
                    setEsignData(result);
                    setActiveTab('digilocker'); 
                } else {
                    alert(result.error || "Aadhaar verification failed.");
                }
            } catch (e) {
                alert("Verification error.");
            } finally {
                setIsDigiLockerLoading(false);
            }
        } else if (event.data?.type === 'DIGILOCKER_ERROR') {
            setIsDigiLockerLoading(false);
            alert("DigiLocker Authentication Cancelled.");
        }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [BACKEND_URL]);

  // --- PREPARE DATA FOR PREVIEW ---
  const stripHtmlToText = (html) => {
    if (!html) return "";
    let text = html.replace(/<br\s*\/?>/gi, "\n");
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = text;
    return tempDiv.textContent || tempDiv.innerText || "";
  };

  const previewData = useMemo(() => {
    if (!letter) return null;

    const recipient = letter.letter_recipients?.[0] || {};
    const bodyMatch = letter.body?.match(/<div class="letter-content">([\s\S]*?)<\/div>/);
    const rawBody = bodyMatch ? bodyMatch[1] : letter.body;
    const cleanBody = stripHtmlToText(rawBody);

    const rawTemplate = letter.Template || letter.template || {}; 
    const template = {
        ...rawTemplate,
        components: rawTemplate.TemplateComponents || rawTemplate.components || [],
        identities: rawTemplate.TemplateIdentities || rawTemplate.identities || []
    };

    const rawSettings = letter.signature_setting || {};
    const sigSettings = Array.isArray(rawSettings) ? rawSettings[0] : rawSettings;
    const rawSigs = letter.signatories || [];
    const signatoriesData = JSON.parse(JSON.stringify(Array.isArray(rawSigs) ? rawSigs : []));

    let currentSigner = null;
    if (token) currentSigner = signatoriesData.find(sig => sig.unique_token === token);
    if (!currentSigner) currentSigner = signatoriesData.find(sig => sig.status === 'PENDING');
    
    // INJECT LIVE SIGNATURE OR DIGILOCKER BADGE
    if (currentSigner) {
        if (activeTab === 'digilocker' && esignData) {
            currentSigner.signature_url = `ESIGN::${JSON.stringify(esignData)}`;
        } else if (drawnSignature) {
            currentSigner.signature_url = drawnSignature;
        } else if (uploadPreview) {
            currentSigner.signature_url = uploadPreview; 
        }
        
        const signerIndex = signatoriesData.findIndex(s => s.unique_token === currentSigner.unique_token);
        if (signerIndex !== -1) {
            signatoriesData[signerIndex] = currentSigner;
        }
    }

    let signatureUrl = null;
    if (sigSettings) {
        if (sigSettings.signature_file?.file_path) signatureUrl = sigSettings.signature_file.file_path;
        else if (sigSettings.SignatureFile?.file_path) signatureUrl = sigSettings.SignatureFile.file_path;
    }
    if (!signatureUrl) {
      const sigMatch = letter.body?.match(/<div class="signature">.*?<img src="([^"]+)".*?<\/div>/);
      signatureUrl = sigMatch ? sigMatch[1].replace(/&amp;/g, '&') : null;
    }
    if (signatureUrl && signatureUrl.startsWith('/uploads')) {
      signatureUrl = `${BACKEND_URL}${signatureUrl}`;
    }

    let sigType = "text";
    if (sigSettings && sigSettings.signature_type) {
        sigType = sigSettings.signature_type;
    } else {
        if (signatoriesData.length > 0) sigType = "send_link";
        else if (signatureUrl) sigType = "upload";
    }

    const state = {
        recipientName: recipient.name || "",
        recipientEmail: recipient.email || "",
        recipientAddress: recipient.address || "",
        subject: letter.subject || "",
        body: cleanBody,
        created_at: (letter.status && letter.status !== "DRAFT") 
          ? (letter.updated_at || letter.updatedAt || letter.created_at || letter.createdAt)
          : (letter.created_at || letter.createdAt),
        signatureType: sigType,
        uploadedSignatureUrl: signatureUrl,
        drawnSignature: null, 
        signaturePlacement: letter.signature_placement?.placement_type || 'last_page',
        signaturePagePoint: letter.signature_placement?.page_from || 0,
        signatories: signatoriesData,
        enableQR: false, 
    };

    const identities = template.identities || [];
    const getIdentity = (key) => identities.find(f => f.field_key === key)?.field_value;
    
    const companyProfile = {
        name: getIdentity('companyName') || "Company",
        email: getIdentity('companyEmail'),
        phone: getIdentity('companyPhone'),
        address: getIdentity('companyAddress')
    };

    return { state, template, companyProfile };
  }, [letter, BACKEND_URL, drawnSignature, uploadPreview, token, esignData, activeTab]);

  const dataURLtoFile = (dataurl, filename) => {
    if (!dataurl) return null;
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) { u8arr[n] = bstr.charCodeAt(n); }
    return new File([u8arr], filename, { type: mime });
  };

  // SUBMIT HANDLER
  const handleSubmit = () => {
    const formData = new FormData();

    if (activeTab === "digilocker") {
      if (!esignData) return alert("Please verify with DigiLocker first.");
      formData.append("esignData", JSON.stringify(esignData));
    } 
    else if (activeTab === "draw") {
      if (!drawnSignature) return alert("Please draw your signature before submitting.");
      formData.append("signatureFile", dataURLtoFile(drawnSignature, 'signature.png'));
    } 
    else {
      if (!uploadFile) return alert("Please upload a signature image before submitting.");
      formData.append("signatureFile", uploadFile);
    }

    setIsSubmitting(true);
    dispatch(submitExternalSignature({ id: letterId, token, formData }))
      .unwrap()
      .then(() => setIsSuccess(true))
      .catch((err) => alert("Error: " + (err.error || err.message || "Submission failed")))
      .finally(() => setIsSubmitting(false));
  };

  // LOADING STATE
  if (isInitLoading) return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center font-sans">
      <div className="relative flex items-center justify-center mb-6">
        <div className="absolute w-20 h-20 bg-blue-100 rounded-full animate-ping opacity-60"></div>
        <div className="w-16 h-16 bg-white shadow-md rounded-full flex items-center justify-center relative z-10">
          <div className="animate-spin rounded-full h-7 w-7 border-4 border-blue-600 border-t-transparent"></div>
        </div>
      </div>
      <h2 className="text-xl font-bold text-slate-800">Establishing Secure Connection</h2>
      <p className="text-slate-500 mt-2 text-sm">Retrieving your document...</p>
    </div>
  );

  // SUCCESS & ALREADY SIGNED SCREEN
  if (isAlreadySigned || isSuccess) return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 font-sans">
      <div className="bg-white p-10 rounded-3xl shadow-xl shadow-slate-200/40 border border-slate-100 text-center max-w-md w-full animate-in zoom-in-95 duration-500">
        <div className="relative mx-auto w-24 h-24 mb-6 flex items-center justify-center">
          <div className="absolute inset-0 bg-emerald-100 rounded-full animate-ping opacity-40"></div>
          <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center relative z-10">
            <CheckCircle2 size={44} className="text-emerald-500" strokeWidth={2.5} />
          </div>
        </div>
        
        <h2 className="text-2xl font-bold text-slate-900 mb-3">
            {isSuccess ? "Document Signed!" : "Already Signed"}
        </h2>
        
        <p className="text-slate-500 leading-relaxed mb-8 text-sm">
            {isSuccess ? (
                <>You have successfully signed this document on <strong className="text-slate-800">{formattedDate}</strong>.</>
            ) : formattedDate ? (
                <>You have already successfully signed this document on <strong className="text-slate-800">{formattedDate}</strong>. No further action is required.</>
            ) : (
                <>You have already provided your signature for this document. No further action is required.</>
            )}
        </p>

        <button onClick={() => window.close()} className="w-full py-3.5 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800 shadow-md transition-all active:scale-[0.98]">
          Close Window
        </button>
      </div>
    </div>
  );

  if (localError && !isAlreadySigned) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6 font-sans">
      <div className="bg-white p-10 rounded-3xl shadow-xl shadow-slate-200/40 border border-slate-100 text-center max-w-md w-full animate-in zoom-in-95 duration-300">
        <div className="mx-auto w-20 h-20 bg-rose-50 rounded-full flex items-center justify-center mb-6">
          <XCircle size={40} className="text-rose-500" strokeWidth={2} />
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-3">Access Denied</h2>
        <p className="text-slate-500 leading-relaxed mb-6">{localError}</p>
        <button onClick={() => window.close()} className="px-6 py-2.5 bg-slate-100 text-slate-700 font-semibold rounded-xl hover:bg-slate-200 transition-colors">
          Close Window
        </button>
      </div>
    </div>
  );

  // MAIN SIGNING SCREEN
  return (
    <>
      <div className="min-h-screen bg-slate-50 py-6 px-4 sm:p-8 flex flex-col items-center font-sans">
        <div className="max-w-6xl w-full flex justify-start items-center mb-8">
           <div className="flex items-center gap-2.5 bg-white px-5 py-2.5 rounded-full shadow-sm border border-slate-200 text-slate-700">
              <ShieldCheck className="text-blue-600" size={18} strokeWidth={2.5} />
              <h1 className="text-xs font-bold uppercase tracking-wider mt-0.5">Secure Signature Request</h1>
           </div>
        </div>

        <div className="max-w-6xl w-full bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-200/60 overflow-hidden flex flex-col lg:flex-row h-[calc(100vh-140px)] min-h-[700px]">
          
          {/* LEFT SIDE: PREVIEW */}
          <div className="lg:w-7/12 bg-slate-100/50 border-r p-4 overflow-y-auto custom-scrollbar flex justify-center">
             {previewData && (
               <div className="w-full max-w-[794px] transform scale-[0.85] origin-top md:scale-[0.9] lg:scale-95 transition-transform">
                  <LetterPreview 
                    state={previewData.state} 
                    selectedTemplate={previewData.template} 
                    companyProfile={previewData.companyProfile} 
                  />
               </div>
             )}
          </div>

          {/* RIGHT SIDE: INPUT */}
          <div className="lg:w-5/12 p-8 sm:p-10 flex flex-col bg-white">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Review & Sign</h2>
              <p className="text-slate-500 text-sm">Please review the document and provide your signature below.</p>
            </div>

            <div className="flex bg-slate-100 p-1 rounded-xl mb-8">
              <button onClick={() => setActiveTab('draw')} className={`flex-1 py-2 text-xs sm:text-sm font-semibold rounded-lg transition-all ${activeTab === 'draw' ? 'bg-white shadow text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}>Draw</button>
              <button onClick={() => setActiveTab('upload')} className={`flex-1 py-2 text-xs sm:text-sm font-semibold rounded-lg transition-all ${activeTab === 'upload' ? 'bg-white shadow text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}>Upload</button>
              <button onClick={() => setActiveTab('digilocker')} className={`flex-1 flex items-center justify-center gap-1 py-2 text-xs sm:text-sm font-semibold rounded-lg transition-all ${activeTab === 'digilocker' ? 'bg-[#4a2bae] shadow text-white' : 'text-slate-500 hover:text-slate-700'}`}>
                  <Fingerprint size={16} /> eSign
              </button>
            </div>

            <div className="flex-1 flex flex-col justify-center mb-8">
              
              {activeTab === 'draw' && (
                <div className="w-full animate-in fade-in zoom-in-95 duration-200">
                  {drawnSignature ? (
                    <div className="flex flex-col items-center">
                      <div className="border-2 border-slate-200 rounded-2xl p-6 mb-4 w-full bg-slate-50 flex justify-center">
                          <img src={drawnSignature} alt="Drawn" className="h-32 object-contain mix-blend-multiply" />
                      </div>
                      <button onClick={() => setDrawnSignature(null)} className="text-rose-500 font-medium text-sm hover:underline">Clear Signature</button>
                    </div>
                  ) : (
                    <button onClick={() => setIsSignatureModalOpen(true)} className="w-full h-48 border-2 border-dashed border-slate-300 bg-slate-50 rounded-2xl flex flex-col items-center justify-center hover:bg-blue-50/50 hover:border-blue-300 transition-colors group">
                      <PenTool className="text-slate-400 group-hover:text-blue-500 mb-3" size={28} />
                      <span className="text-slate-600 font-semibold group-hover:text-blue-600">Click to Open Signature Pad</span>
                    </button>
                  )}
                </div>
              )}

              {activeTab === 'upload' && (
                <div className="w-full animate-in fade-in zoom-in-95 duration-200">
                   <input type="file" id="upload-sig" className="hidden" accept="image/png, image/jpeg" onChange={(e) => setUploadFile(e.target.files[0])}/>
                   {uploadPreview ? (
                      <div className="flex flex-col items-center">
                          <div className="border-2 border-slate-200 rounded-2xl p-6 mb-4 w-full bg-slate-50 flex justify-center">
                              <img src={uploadPreview} alt="Preview" className="h-32 object-contain mix-blend-multiply" />
                          </div>
                          <label htmlFor="upload-sig" className="text-blue-600 font-medium text-sm cursor-pointer hover:underline">Upload Different Image</label>
                      </div>
                   ) : (
                      <label htmlFor="upload-sig" className="w-full h-48 border-2 border-dashed border-slate-300 bg-slate-50 rounded-2xl flex flex-col items-center justify-center hover:bg-blue-50/50 hover:border-blue-300 transition-colors group cursor-pointer">
                          <Upload className="text-slate-400 group-hover:text-blue-500 mb-3" size={28} />
                          <span className="text-slate-600 font-semibold group-hover:text-blue-600">Click to Browse Images</span>
                          <span className="text-slate-400 text-xs mt-1">Accepts PNG or JPG</span>
                      </label>
                   )}
                </div>
              )}

              {activeTab === 'digilocker' && (
                <div className="w-full text-center animate-in fade-in zoom-in-95 duration-200">
                   {esignData ? (
                      <div className="border border-emerald-200 bg-emerald-50 rounded-2xl p-6 flex flex-col items-center">
                          <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-3">
                              <CheckCircle2 className="text-emerald-600" size={24} />
                          </div>
                          <h3 className="font-bold text-slate-800">Verified via Aadhaar</h3>
                          <p className="text-sm text-slate-500 mt-1">Signer: {esignData.signerName}</p>
                          <p className="text-xs text-slate-400 mt-1">Ref: {esignData.transactionId}</p>
                      </div>
                   ) : (
                      <div className="border-2 border-[#4a2bae]/20 bg-[#4a2bae]/5 rounded-2xl p-8 flex flex-col items-center justify-center h-48">
                          <p className="text-slate-600 text-sm mb-4">Secure your signature using Aadhaar-based eSign.</p>
                          <button 
                              onClick={handleDigiLockerInit}
                              disabled={isDigiLockerLoading}
                              className="bg-[#4a2bae] text-white px-6 py-3 rounded-xl font-bold hover:bg-[#3b228c] transition-colors flex items-center gap-2 shadow-lg shadow-[#4a2bae]/20 disabled:opacity-70"
                          >
                              {isDigiLockerLoading ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                              ) : (
                                <Fingerprint size={18} />
                              )}
                              {isDigiLockerLoading ? "Connecting..." : "Verify with DigiLocker"}
                          </button>
                      </div>
                   )}
                </div>
              )}
            </div>

            <div className="mt-auto">
              <button 
                onClick={handleSubmit} 
                disabled={
                    isSubmitting || 
                    (activeTab === 'draw' && !drawnSignature) || 
                    (activeTab === 'upload' && !uploadFile) ||
                    (activeTab === 'digilocker' && !esignData)
                } 
                className="w-full py-4 rounded-xl bg-slate-900 text-white font-bold hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg flex justify-center items-center gap-2 transition-all active:scale-95"
              >
                {isSubmitting ? <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div> : <CheckCircle2 size={20} />}
                {isSubmitting ? "Securing Document..." : "Accept & Sign Document"}
              </button>
              <p className="text-center text-xs text-slate-400 mt-4 flex justify-center items-center gap-1 font-medium">
                <ShieldCheck size={14} /> Protected by 256-bit encryption
              </p>
            </div>
          </div>
        </div>

        {isSignatureModalOpen && (
          <SignatureModal onClose={() => setIsSignatureModalOpen(false)} onSave={(data) => { setDrawnSignature(data); setIsSignatureModalOpen(false); }} />
        )}
      </div>

      <style>{`
        @keyframes ping {
          75%, 100% { transform: scale(1.5); opacity: 0; }
        }
        .animate-ping {
          animation: ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-in {
          animation-duration: 0.5s;
          animation-fill-mode: both;
        }
        .zoom-in-95 {
          animation-name: zoomIn;
        }
        @keyframes zoomIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .fade-in {
          animation-name: fadeIn;
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.98);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .hidden {
          display: none;
        }
      `}</style>
    </>
  );
}